import React, { forwardRef } from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'

import Spinner from './Spinner'
import Icon from './Icon'

const PrimaryButton = forwardRef(
    (
        {
            id,
            children,
            isSmall,
            isAnimated = true,
            iconName,
            isLoading,
            isAddonBefore,
            isAddonAfter,
            hasAddonBefore,
            hasAddonAfter,
            ...props
        },
        ref,
    ) => {
        const { iconPosition = 'left' } = props
        const iconIsSet = typeof iconName !== 'undefined'
        const isIconButton = typeof children === 'undefined' && iconIsSet
        return (
            <StyledButton
                id={id}
                ref={ref}
                whileTap={{ scale: isAnimated ? 1 : 0.95 }}
                $isAddonBefore={isAddonBefore}
                $isAddonAfter={isAddonAfter}
                $hasAddonBefore={hasAddonBefore}
                $hasAddonAfter={hasAddonAfter}
                $isSmall={isSmall}
                {...props}
            >
                {iconIsSet && !isLoading && iconPosition === 'left' && (
                    <StyledIconLeft
                        isSmaller
                        name={iconName}
                        $isIconButton={isIconButton}
                    />
                )}
                {isLoading && (
                    <SpinnerContainer>
                        <Spinner size={12} color="white" />
                    </SpinnerContainer>
                )}
                <div>{children}</div>
                {iconIsSet && iconPosition === 'right' && (
                    <StyledIconRight
                        isSmaller
                        name={iconName}
                        $isIconButton={isIconButton}
                    />
                )}
            </StyledButton>
        )
    },
)

export const StyledButton = styled(motion.button)(
    ({
        theme,
        $isSmall,
        $isAddonBefore,
        $isAddonAfter,
        $hasAddonBefore,
        $hasAddonAfter,
    }) => `
    display: flex;
    background-color: ${theme.colorPrimary};
    border-color: ${theme.colorPrimary};
    border-top-width : ${theme.defaultBorderWidth}rem;
    border-bottom-width : ${theme.defaultBorderWidth}rem;
    border-left-width : ${$isAddonAfter ? 0 : theme.defaultBorderWidth}rem;
    border-right-width : ${$isAddonBefore ? 0 : theme.defaultBorderWidth}rem;
    border-top-left-radius: ${
        $hasAddonBefore || $isAddonAfter ? 0 : theme.defaultButtonBorderRadius
    }rem;
    border-bottom-left-radius: ${
        $hasAddonBefore || $isAddonAfter ? 0 : theme.defaultButtonBorderRadius
    }rem;
    border-top-right-radius: ${
        $hasAddonAfter || $isAddonBefore ? 0 : theme.defaultButtonBorderRadius
    }rem;
    border-bottom-right-radius: ${
        $hasAddonAfter || $isAddonBefore ? 0 : theme.defaultButtonBorderRadius
    }rem;
    color: ${theme.colorWhite};    
    padding: ${
        $isSmall
            ? `${theme.spacingSmallest}rem ${theme.spacingSmaller}rem`
            : `${theme.spacingSmaller}rem ${theme.spacingSmall}rem`
    };
    font-size: ${theme.fontSize}rem;
    font-weight: ${$isSmall ? theme.fontWeightMedium : theme.fontWeightBold};
    justify-content: center;
    align-items: center;
    :hover:not(:disabled) {
        background-color: ${theme.colorPrimaryDark};
        border-color: ${theme.colorPrimaryDark};
    }
    :disabled {
        cursor: not-allowed;
        background-color: ${theme.colorPrimaryLighter};
        border-color: ${theme.colorPrimaryLighter};
    }
`,
)

const StyledIconLeft = styled(Icon)(
    ({ theme, $isIconButton }) => `
    margin-right: ${!$isIconButton && theme.spacingSmaller}rem;
`,
)

const SpinnerContainer = styled.span(
    ({ theme }) => `
    font-size: ${theme.fontSizeSmall}rem;
    height: 12px;
    margin-right: ${theme.spacingSmaller}rem;
`,
)

const StyledIconRight = styled(Icon)(
    ({ theme, $isIconButton }) => `
    margin-left: ${!$isIconButton && theme.spacingSmall}rem;
`,
)

export default PrimaryButton
